import { memo, useMemo } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'

import {
  CategoryChildrens,
  CategoryParent,
} from '@entities/Categories'

import { useAddressStore } from '@features/shipping-methods'


import { IMAGE_SIZES } from '@shared/contants'
import { useCategoriesBranchQuery, useCategoryQuery } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import Accordion from '@shared/ui/accordion'

import styles from './styles.module.scss'

interface CategoriesBranchProps {
  className?: string;
}

// Мемоизированный компонент загрузки
const CategoriesBranchLoading = memo(function CategoriesBranchLoading () {
  return (
    <SkeletonTheme>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {Array.from({ length: 12 }).map((_, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Skeleton
              count={1}
              width={32}
              height={32}
              style={{ borderRadius: 8 }}
            />
            <Skeleton
              count={1}
              width={index % 2 === 0 ? 130 : 100}
              height={16}
              style={{ borderRadius: 6 }}
            />
          </div>
        ))}
      </div>
    </SkeletonTheme>
  )
})

// Мемоизированный основной компонент
const CategoriesBranch = memo(function CategoriesBranch (
  props: CategoriesBranchProps,
) {
  const { categorySlug } = useParams()
  const { className } = props
  const { shopId } = useAddressStore()

  // Fetch current category to get parent info from backend
  const { data: currentCategoryData } = useCategoryQuery({
    variables: {
      shopId,
      slug: categorySlug,
    },
    skip: !categorySlug,
  })

  // Запрос данных
  const { data, loading } = useCategoriesBranchQuery({
    variables: {
      shopId: shopId,
      style: IMAGE_SIZES.SMALL,
    },
  })

  // Determine active parent slug and active child slug from backend data
  const { activeParentSlug, activeChildSlug } = useMemo(() => {
    const currentCategory = currentCategoryData?.category
    if (!currentCategory) {
      return { activeParentSlug: categorySlug, activeChildSlug: undefined }
    }

    // If category has a parent, the parent should be open and this category is the active child
    if (currentCategory.parent?.slug) {
      return {
        activeParentSlug: currentCategory.parent.slug,
        activeChildSlug: currentCategory.slug,
      }
    }

    // If no parent, this is a top-level category - open its accordion
    return { activeParentSlug: currentCategory.slug, activeChildSlug: undefined }
  }, [ currentCategoryData, categorySlug ])

  // Мемоизация списка категорий с сортировкой (текущая категория первой)
  const categories = useMemo(() => {
    const items = data?.categoriesBranch || []
    if (!activeParentSlug) return items

    return [ ...items ].sort((a, b) => {
      if (a?.slug === activeParentSlug) return -1
      if (b?.slug === activeParentSlug) return 1
      return 0
    })
  }, [ data?.categoriesBranch, activeParentSlug ])

  // Мемоизация рендеринга элементов
  const renderedItems = useMemo(() => {
    return categories.map((item) => {
      const isActive = activeParentSlug === item?.slug

      return (
        <div className={styles.root__item} key={`${item?.id}-${activeParentSlug}`}>
          <Accordion
            className={styles.accordion}
            triggerClassName={cn(
              styles['root__parent-link'],
              isActive && styles['root__parent-link--active'],
            )}
            trigger={
              <CategoryParent
                name={item?.name as string}
                iconUrl={item?.miniIcon?.url ?? item?.icon?.url}
              />
            }
            content={
              <CategoryChildrens
                items={item?.childrens}
                activeChildSlug={isActive ? (activeChildSlug as string) : undefined}
              />
            }
            arrowIcon={false}
            defaultOpen={isActive}
          />
        </div>
      )
    })
  }, [ categories, activeParentSlug, activeChildSlug ])

  if (loading) {
    return <CategoriesBranchLoading />
  }

  return <div className={cn(styles.root, className)}>{renderedItems}</div>
})

export default CategoriesBranch
